import type { CommandMentionMeta, MentionToken } from '@/types/conversation-input';
import type { VisualStyle } from '@/types';

const VISUAL_STYLE_DESCRIPTION = '选择需要的视觉风格应用';
const VISUAL_STYLE_REQUIRED_REASON = '请选择视觉风格';
const VISUAL_STYLE_PREFIX = '/视觉风格';

export const CUSTOM_VISUAL_STYLE_ID = '__custom__';

export const CUSTOM_VISUAL_STYLE: VisualStyle = {
  styleId: CUSTOM_VISUAL_STYLE_ID,
  categoryId: CUSTOM_VISUAL_STYLE_ID,
  name: '自定义',
  thumbnailUrl: null,
  isSystem: false,
  orderIndex: -1,
};
const CHAR_THREE_VIEW_DISPLAY = '/角色形象图[三视图]';
const PROP_THREE_VIEW_DISPLAY = '/道具外观图[三视图]';
const VISUAL_STYLE_COMMAND_PATTERN = /\/视觉风格(?:\[[^\]]+\])?/;
const CHARACTER_IMAGE_COMMAND_PATTERN =
  /\/角色形象图\[(?:三视图|真人版|真人|表情版)\]|\/角色形象三视图/;
const PROP_THREE_VIEW_COMMAND_PATTERN = /\/道具外观图(?:\[三视图\])?/;

interface VisualStyleCommandStateParams {
  hasVisualStyleCommand: boolean;
  selectedStyleName?: string | null;
}

export interface ResolveVisualStyleSubmitValueParams {
  mentionTokens: MentionToken[];
}

export function findVisualStyleCommandToken(
  mentionTokens: MentionToken[]
): MentionToken | undefined {
  return mentionTokens.find(
    (token) => token.kind === 'command' && token.commandKey === 'visualStyle'
  );
}

export function formatVisualStyleDisplay(styleName?: string | null): string {
  const normalizedName = styleName?.trim();
  return normalizedName ? `${VISUAL_STYLE_PREFIX}[${normalizedName}]` : VISUAL_STYLE_PREFIX;
}

export function buildVisualStyleCommandMeta(
  style?: Pick<VisualStyle, 'styleId' | 'name'> | null
): CommandMentionMeta | undefined {
  if (!style) return undefined;
  return {
    styleId: style.styleId,
    styleName: style.name,
  };
}

export function resolveVisualStyleCommandDisplay(
  style?: Pick<VisualStyle, 'name'> | null,
  fallbackStyleName?: string | null
): string {
  return formatVisualStyleDisplay(style?.name ?? fallbackStyleName);
}

export function composeAppliedPromptWithProjectCommands(params: {
  prompt: string;
  defaultVisualStyle?: Pick<VisualStyle, 'styleId' | 'name'> | null;
  defaultVisualStyleId?: string | null;
  resourceType?: 'character' | 'location' | 'prop' | 'footage' | null;
  suppressNonVisualStyleCommands?: boolean;
}): string {
  const prompt = params.prompt.trim();
  const prefixSegments: string[] = [];
  const suffixSegments: string[] = [];

  if (
    (params.defaultVisualStyle || params.defaultVisualStyleId) &&
    !VISUAL_STYLE_COMMAND_PATTERN.test(prompt)
  ) {
    prefixSegments.push(
      params.defaultVisualStyle
        ? resolveVisualStyleCommandDisplay(params.defaultVisualStyle)
        : formatVisualStyleDisplay()
    );
  }

  if (!params.suppressNonVisualStyleCommands) {
    if (params.resourceType === 'character' && !CHARACTER_IMAGE_COMMAND_PATTERN.test(prompt)) {
      suffixSegments.push(CHAR_THREE_VIEW_DISPLAY);
    }
    if (params.resourceType === 'prop' && !PROP_THREE_VIEW_COMMAND_PATTERN.test(prompt)) {
      suffixSegments.push(PROP_THREE_VIEW_DISPLAY);
    }
  }

  const segments = [...prefixSegments];
  if (prompt) {
    segments.push(prompt);
  }
  segments.push(...suffixSegments);

  return segments.join('\n');
}

export function resolveVisualStylePickerSelectedId(params: {
  tokenStyleId?: string | null;
  defaultStyleId?: string | null;
}): string | null {
  return params.tokenStyleId ?? params.defaultStyleId ?? null;
}

export function getVisualStyleCommandState({
  hasVisualStyleCommand,
  selectedStyleName,
}: VisualStyleCommandStateParams): {
  disabled: boolean;
  applied: boolean;
  description: string;
} {
  return {
    disabled: hasVisualStyleCommand,
    applied: hasVisualStyleCommand,
    description: selectedStyleName ? `当前风格：${selectedStyleName}` : VISUAL_STYLE_DESCRIPTION,
  };
}

export function resolveVisualStyleSubmitValue({
  mentionTokens,
}: ResolveVisualStyleSubmitValueParams): {
  hasCommand: boolean;
  styleId?: string;
  styleName?: string;
  isCustomStyle: boolean;
} {
  const commandToken = findVisualStyleCommandToken(mentionTokens);
  const rawStyleId = commandToken?.commandMeta?.styleId;
  const isCustomStyle = rawStyleId === CUSTOM_VISUAL_STYLE_ID;

  return {
    hasCommand: Boolean(commandToken),
    styleId: isCustomStyle ? undefined : rawStyleId,
    styleName: commandToken?.commandMeta?.styleName,
    isCustomStyle,
  };
}

export function getVisualStyleUnavailableReason(): string {
  return VISUAL_STYLE_REQUIRED_REASON;
}
